import React from "react";
import Draggable from "react-draggable";
import "./ChoiceModal.css";

/**
 * A modal that asks the user to pick between two or more named actions, each with a short
 * consequence note, plus an optional cancel. Use it instead of ConfirmationModal when the
 * choice is not a yes/no — e.g. "update the existing record" vs "create a new one".
 *
 * Wrap it in a <Portal> the same way ConfirmationModal is used.
 *
 * choices: [{ value, label, note, tone, disabled }] — tone "primary" fills the recommended action.
 * comparison: optional { from, to, changeLabel } showing what changed, stacked one per line.
 * prompt: optional line above the options telling the user to pick one.
 * fields: optional [{ key, label, value, invalid }] rendered inline so the user can correct
 *   values without leaving the modal; edits are reported through onFieldChange(key, value).
 * onBack: optional handler adding a Back button beside Cancel, for a multi-step modal.
 * hideCancel: drops the footer Cancel button when the choices themselves already cover declining.
 */
const ChoiceModal = ({
    heading,
    message,
    comparison,
    prompt,
    fields = [],
    onFieldChange,
    choices = [],
    onSelection,
    onCancel,
    cancelLabel = "Cancel",
    hideCancel = false,
    onBack,
    backLabel = "Back"
}) => (
    <div id="modalId" className="modal modal-region choice-modal" style={{ display: "block" }} role="dialog" aria-hidden="true">
        <Draggable cancel=".not-draggable">
            <div className="modal-dialog draggable" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h2 className="choice-modal__heading">{heading}</h2>
                        {message && <p className="choice-modal__message">{message}</p>}
                        <button type="button" className="close not-draggable" aria-label="Close" onClick={onCancel}>
                            <span aria-hidden="true">
                                <img src="/img/close.svg" alt="" />
                            </span>
                        </button>
                    </div>
                    <div className="modal-body region-otr not-draggable">
                        {comparison && (
                            <div className="choice-modal__names">
                                <span className="choice-modal__name" title={comparison.from}>
                                    {comparison.from || "—"}
                                </span>
                                <span className="choice-modal__change-label">
                                    {comparison.changeLabel || "will change to"}
                                </span>
                                <span className="choice-modal__name choice-modal__name--new" title={comparison.to}>
                                    {comparison.to || "—"}
                                </span>
                            </div>
                        )}
                        {!!fields.length && (
                            <div className="choice-modal__fields">
                                {fields.map(({ key, label, value, invalid }) => (
                                    <label key={key} className="choice-modal__field">
                                        <span className="choice-modal__field-label">{label}</span>
                                        <input
                                            type="text"
                                            className={`choice-modal__field-input ${invalid ? "choice-modal__field-input--invalid" : ""}`}
                                            value={value || ""}
                                            onChange={e => onFieldChange(key, e.target.value)}
                                        />
                                    </label>
                                ))}
                            </div>
                        )}
                        {prompt && <p className="choice-modal__prompt">{prompt}</p>}
                        <div className={`choice-modal__options ${choices.every(({ note }) => !note) ? "choice-modal__options--compact" : ""}`}>
                            {choices.map(({ value, label, note, tone, disabled }) => (
                                <button
                                    key={value}
                                    type="button"
                                    disabled={disabled}
                                    className={`choice-modal__option ${tone === "primary" ? "choice-modal__option--primary" : ""}`}
                                    onClick={() => onSelection(value)}
                                >
                                    <span className="choice-modal__option-label">{label}</span>
                                    {note && <span className="choice-modal__option-note">{note}</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                    {((onCancel && !hideCancel) || onBack) && (
                        <div className="btnOtr not-draggable">
                            {onBack && (
                                <button type="button" className="btn choice-modal__cancel choice-modal__back" onClick={onBack}>
                                    {backLabel}
                                </button>
                            )}
                            {onCancel && !hideCancel && (
                                <button type="button" className="btn choice-modal__cancel" onClick={onCancel}>
                                    {cancelLabel}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Draggable>
    </div>
);

export default ChoiceModal;
